import { MutableRefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAppSelector } from '../../../../redux/hooks';
import AccountSearch from '../../../../components/custom-input/AccountSearch';
import { Loader } from '../../../../components';
import ReportSummaryCard from '../../../../components/report-summary-card/ReportSummaryCard';
import PagedTable, { type PagedTableHeader } from '../../../../components/sorted-table/PagedTable';
import {
  downloadBreederUpcomingPaymentsReport,
  getBreederUpcomingPaymentSummary,
  getBreederUpcomingPayments,
} from '../../../../api/marketPlacePaymentTransactionOrderDetailApi';
import { showToast } from '../../../../services/toast.service';
import { toastMessages } from '../../../../constants/errorMessages';
import { BREEDER_PAYMENTS_CONSTANTS, LABEL_CONSTANTS, TABLE_HEADER_CONSTANTS } from '../../../../constants/common';
import type { StandardParams } from '../../../../types/interfaces/apiParams.interfaces';
import type { Account } from '../../../../types/interfaces/account.interfaces';
import type { BreederUpcomingPaymentListItem } from '../../../../types/interfaces/marketPlace.interfaces';
import { centsToDollars } from '../../../../services/account.service';
import { DownloadSVG } from '../../../../components/svgs';
import { dateFormatMMDDYYYY } from '../../../../utils/commonUtils';
import './upcomingPayments.scss';

const UPCOMING_PAYMENTS_PAGE_SIZE = 10;

export interface BreederPaymentRow {
  id: string;
  breederName: string;
  email: string;
  orders: number;
  grossAmountCents: number;
  commissionCents: number;
  netPayableCents: number;
}

const UPCOMING_PAGED_HEADERS: PagedTableHeader[] = [
  { displayName: TABLE_HEADER_CONSTANTS.BREEDER_NAME },
  { displayName: TABLE_HEADER_CONSTANTS.EMAIL_ADDRESS },
  { displayName: TABLE_HEADER_CONSTANTS.ORDERS },
  { displayName: TABLE_HEADER_CONSTANTS.GROSS_AMOUNT },
  { displayName: TABLE_HEADER_CONSTANTS.COMMISSION },
  { displayName: TABLE_HEADER_CONSTANTS.NET_PAYABLE },
];

const mapApiItemToRow = (item: BreederUpcomingPaymentListItem): BreederPaymentRow => ({
  id: String(item.sellerAccountId),
  breederName: item.sellerAccount?.name ?? '',
  email: item.sellerAccount?.contactEmail ?? '',
  orders: item.numberOfOrders ?? 0,
  grossAmountCents: item.totalAmountCents ?? 0,
  commissionCents: item.rsgCommissionCents ?? 0,
  netPayableCents: item.netPayableCents ?? 0,
});

interface BreederPaymentSummaryState {
  totalPendingAmountCents: number;
  totalBreedersPending: number;
  totalOrdersPending: number;
}

const DEFAULT_SUMMARY: BreederPaymentSummaryState = {
  totalPendingAmountCents: 0,
  totalBreedersPending: 0,
  totalOrdersPending: 0,
};

interface BreederPaymentCardProps {
  row: BreederPaymentRow;
}

const formatUsdFromCents = (amountCents?: number): string => `$${centsToDollars(amountCents ?? 0)}`;

const BreederPaymentCard = ({ row }: BreederPaymentCardProps): JSX.Element => (
  <div className="card max-width breeder-payments__card">
    <div className="breeder-payments-card">
      <div className="breeder-payments-card__content">
        <div className="breeder-payments-card__grid">
          <div className="card-content-section">
            <div className="left column">
              <label>{TABLE_HEADER_CONSTANTS.BREEDER_NAME}</label>
              <label>{row.breederName}</label>
            </div>
            <div className="left column">
              <label>{TABLE_HEADER_CONSTANTS.EMAIL_ADDRESS}</label>
              <label>{row.email}</label>
            </div>
            <div className="right column">
              <label>{TABLE_HEADER_CONSTANTS.ORDERS}</label>
              <label>{row.orders}</label>
            </div>
          </div>
          <div className="card-content-section">
            <div className="left column">
              <label>{TABLE_HEADER_CONSTANTS.GROSS_AMOUNT}</label>
              <label>{formatUsdFromCents(row.grossAmountCents)}</label>
            </div>
            <div className="left column">
              <label>{TABLE_HEADER_CONSTANTS.COMMISSION}</label>
              <label>{formatUsdFromCents(row.commissionCents)}</label>
            </div>
            <div className="right column">
              <label>{TABLE_HEADER_CONSTANTS.NET_PAYABLE}</label>
              <label>{formatUsdFromCents(row.netPayableCents)}</label>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

const UpcomingPayments = (): JSX.Element => {
  const accountId = useAppSelector(state => state.activeAccount.accountId);
  const [entitySearchAccount, setEntitySearchAccount] = useState<Account | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState<BreederPaymentSummaryState>(DEFAULT_SUMMARY);
  const [rows, setRows] = useState<BreederPaymentRow[]>([]);
  const [mobileOffset, setMobileOffset] = useState(0);
  const [tableRefresh, setTableRefresh] = useState(0);
  const [isExporting, setIsExporting] = useState(false);

  const mobileCallNumber = useRef(0);
  const hasAllMobileResults: MutableRefObject<boolean> = useRef(false);
  const isLoadingAdditionalMobile: MutableRefObject<boolean> = useRef(false);
  const mobileOffsetRef = useRef(0);

  useEffect(() => {
    mobileOffsetRef.current = mobileOffset;
  }, [mobileOffset]);

  const upcomingBreederFilterParams = useMemo((): { accountId?: number } => {
    if (entitySearchAccount?.accountId != null) {
      return { accountId: entitySearchAccount.accountId };
    }
    return {};
  }, [entitySearchAccount?.accountId]);

  const fetchSummary = useCallback(() => {
    if (accountId == null) return;
    const query = Object.keys(upcomingBreederFilterParams).length > 0 ? upcomingBreederFilterParams : undefined;
    getBreederUpcomingPaymentSummary(query)
      .then(response => {
        const data = response.data;
        if (data) {
          setSummary({
            totalPendingAmountCents: data.totalPendingAmountCents ?? 0,
            totalBreedersPending: data.totalBreedersPending ?? 0,
            totalOrdersPending: data.totalOrdersPending ?? 0,
          });
        }
      })
      .catch(() => {
        showToast.error(toastMessages.SOMETHING_WENT_WRONG);
        setSummary(DEFAULT_SUMMARY);
      });
  }, [accountId, upcomingBreederFilterParams]);

  const loadMobileData = useCallback(
    async (offsetOverride?: number) => {
      if (accountId == null) return;
      if (globalThis.innerWidth > 767) return;
      const offset = offsetOverride ?? mobileOffsetRef.current;
      if (offset > 0 && hasAllMobileResults.current) return;

      mobileCallNumber.current++;
      const callNum = mobileCallNumber.current;

      try {
        setLoading(true);
        const { data: response } = await getBreederUpcomingPayments({
          limit: UPCOMING_PAYMENTS_PAGE_SIZE,
          offset,
          ...upcomingBreederFilterParams,
        });
        if (callNum !== mobileCallNumber.current) return;
        const data = response ?? [];
        setRows(prev => (offset === 0 ? data.map(mapApiItemToRow) : [...prev, ...data.map(mapApiItemToRow)]));
        hasAllMobileResults.current = data.length === 0 || data.length < UPCOMING_PAYMENTS_PAGE_SIZE;
      } catch {
        showToast.error(toastMessages.SOMETHING_WENT_WRONG);
        if (offset === 0) {
          setRows([]);
        }
      } finally {
        setLoading(false);
      }
    },
    [accountId, upcomingBreederFilterParams],
  );

  const loadMoreMobile = useCallback(() => {
    setMobileOffset(prev => prev + UPCOMING_PAYMENTS_PAGE_SIZE);
  }, []);

  const handleMobileScroll = useCallback(() => {
    if (globalThis.innerWidth > 767) return;
    const docEl = document.documentElement;
    const body = document.body;
    const scrollTop = globalThis.pageYOffset ?? docEl.scrollTop ?? body.scrollTop ?? 0;
    const scrollHeight = Math.max(docEl.scrollHeight, body.scrollHeight);
    const clientHeight = docEl.clientHeight || globalThis.innerHeight;
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
    const atBottom = distanceFromBottom <= clientHeight;
    if (atBottom && !isLoadingAdditionalMobile.current && !hasAllMobileResults.current) {
      isLoadingAdditionalMobile.current = true;
      loadMoreMobile();
    }
  }, [loadMoreMobile]);

  useEffect(() => {
    if (accountId == null) return;
    fetchSummary();
    setTableRefresh(previous => previous + 1);
  }, [accountId, fetchSummary, upcomingBreederFilterParams]);

  useEffect(() => {
    if (accountId == null) return;
    hasAllMobileResults.current = false;
    setRows([]);
    setMobileOffset(0);
    globalThis.innerWidth <= 767 && void loadMobileData(0);
  }, [accountId, tableRefresh, loadMobileData]);

  useEffect(() => {
    if (globalThis.innerWidth > 767) return;
    if (mobileOffset === 0) return;
    void loadMobileData();
  }, [mobileOffset, loadMobileData]);

  useEffect(() => {
    isLoadingAdditionalMobile.current = false;
  }, [rows]);

  useEffect(() => {
    const opts: AddEventListenerOptions = { passive: true };
    globalThis.addEventListener('scroll', handleMobileScroll, opts);
    globalThis.addEventListener('touchmove', handleMobileScroll, opts);
    return () => {
      globalThis.removeEventListener('scroll', handleMobileScroll, opts);
      globalThis.removeEventListener('touchmove', handleMobileScroll, opts);
    };
  }, [handleMobileScroll]);

  const getUpcomingPaymentsPaged = useCallback(
    (params: StandardParams) => {
      return getBreederUpcomingPayments({
        limit: params.limit,
        offset: params.offset,
        ...upcomingBreederFilterParams,
      });
    },
    [upcomingBreederFilterParams],
  );

  const getExportODataParams = useCallback((): { sellerAccountId?: number } => {
    if (entitySearchAccount?.accountId != null) {
      return { sellerAccountId: entitySearchAccount.accountId };
    }
    return {};
  }, [entitySearchAccount?.accountId]);

  const setExportReportName = useCallback((link: HTMLAnchorElement): void => {
    const prefix = 'Breeder_Upcoming_Payments';
    const currentDate = dateFormatMMDDYYYY(new Date());
    const fileName = `${prefix}_${currentDate}.xlsx`;
    link.setAttribute('download', fileName);
  }, []);

  const handleExportData = useCallback(async () => {
    if (isExporting) return;
    try {
      setIsExporting(true);
      const response = await downloadBreederUpcomingPaymentsReport({}, getExportODataParams());
      const blob = new Blob([response.data], { type: response.headers['content-type'] });
      const url = globalThis.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      setExportReportName(link);
      document.body.appendChild(link);
      link.click();
    } catch {
      showToast.error(toastMessages.FAILED_TO_DOWNLOAD_REPORT);
    } finally {
      setIsExporting(false);
    }
  }, [isExporting, getExportODataParams, setExportReportName]);

  return (
    <>
      <div className="breeder-payments__filters">
        <div className="breeder-payments__filter-group breeder-payments__filter-group--entity-search">
          <label className="breeder-payments__filter-label">{BREEDER_PAYMENTS_CONSTANTS.SEARCH_BY}</label>
          <div className="breeder-payments__search-combined breeder-payments__search-combined--account-only">
            <div className="breeder-payments__search-combined-input">
              <AccountSearch
                key="upcoming-payments-account-buyer"
                account={entitySearchAccount}
                onChange={setEntitySearchAccount}
                placeholder={BREEDER_PAYMENTS_CONSTANTS.SEARCH_BREEDER_PLACEHOLDER}
              />
            </div>
          </div>
        </div>
        <div className="breeder-payments__download-action">
          <button
            type="button"
            className={`button outlined download-report breeder-payments__download ${isExporting ? 'disabled' : ''}`}
            onClick={handleExportData}
            disabled={isExporting}>
            <DownloadSVG />
            &nbsp;{LABEL_CONSTANTS.DOWNLOAD}
          </button>
        </div>
      </div>

      <div className="breeder-payments__content">
        <div className="breeder-payments__summary">
          <div className="breeder-payments__summary-cards">
            <ReportSummaryCard
              title={BREEDER_PAYMENTS_CONSTANTS.TOTAL_PENDING_AMOUNT}
              value={formatUsdFromCents(summary.totalPendingAmountCents)}
              subtext={BREEDER_PAYMENTS_CONSTANTS.ALL_BREEDERS}
              valueHighlight
            />
            <ReportSummaryCard
              title={BREEDER_PAYMENTS_CONSTANTS.TOTAL_BREEDERS_PENDING}
              value={summary.totalBreedersPending}
              subtext={BREEDER_PAYMENTS_CONSTANTS.AWAITING_PAYMENT}
            />
            <ReportSummaryCard
              title={BREEDER_PAYMENTS_CONSTANTS.TOTAL_ORDERS_PENDING}
              value={summary.totalOrdersPending}
              subtext={BREEDER_PAYMENTS_CONSTANTS.SETTLEMENT_PENDING}
            />
          </div>
        </div>

        <div className="desk-breeder-payments">
          {accountId != null && (
            <PagedTable
              headers={UPCOMING_PAGED_HEADERS}
              getData={getUpcomingPaymentsPaged}
              buildRow={(item: BreederUpcomingPaymentListItem) => {
                const row = mapApiItemToRow(item);
                return [
                  row.breederName,
                  row.email,
                  String(row.orders),
                  formatUsdFromCents(row.grossAmountCents),
                  formatUsdFromCents(row.commissionCents),
                  formatUsdFromCents(row.netPayableCents),
                ];
              }}
              height={500}
              minHeight={156}
              pageSize={UPCOMING_PAYMENTS_PAGE_SIZE}
              scrollBufferThickness={100}
              refresh={tableRefresh}
              setRefresh={setTableRefresh}
              noResultsMessage={LABEL_CONSTANTS.NO_RESULTS_FOUND}
            />
          )}
        </div>

        <div className="xs-breeder-payments">
          {!loading && rows.length === 0 ? (
            <div className="breeder-payments__no-results">{LABEL_CONSTANTS.NO_RESULTS_FOUND}</div>
          ) : (
            rows.length > 0 && rows.map(row => <BreederPaymentCard key={row.id} row={row} />)
          )}
          {loading && <Loader addedSpace loaderSize="small" />}
        </div>
      </div>
    </>
  );
};

export default UpcomingPayments;
